import { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import { Team01, StoryFilterKey, RallyBadge } from "./GameStoryDialog_UPDATED";
import { InsightsJson } from "../../shared/types/insightsEtcTypes";
import { CvJson } from "../../shared/types/cvJson";
import { PounceWindowRow } from "../../shared/types/pounceEtc";

export type TriagePlayerId = 0 | 1 | 2 | 3;
export const TRIAGE_PLAYER_IDS: TriagePlayerId[] = [0, 1, 2, 3];

// export type AssignmentBucketKey = "assigned_wins" | "assigned_losses";
export type AssignmentBucketKey =
  | "game_points_won"
  | "game_points_lost"
  | "opportunities_lost"
  | "opponent_opportunities_stymied";

export type AssignmentKind = "credit" | "blame";

export type AssignmentReasonCode =
  | StoryFilterKey
  | "terminal_winner"
  | "forced_error_drawn"
  | "setup_shot"
  | "terminal_error"
  | "unknown";

export type AssignmentSupportSignal = {
  key: string;
  value: string;
};

export type GamePointAssignmentExample = {
  rallyIndex: number;
  bucketKey: AssignmentBucketKey;
  assignmentKind: AssignmentKind;

  team: Team01;
  assignedPlayerId: TriagePlayerId;

  label: string;
  reasonCode: AssignmentReasonCode;
  reasonLabel: string;
  reasonText: string;
  focusShotNumber: number | null;

  rationale: string[];
  supportSignals: AssignmentSupportSignal[];

  scoreBefore: [number, number] | null;
  scoreAfter: [number, number] | null;
  servingTeam: Team01 | null;
  winnerTeam: Team01 | null;
  gamePointTeamsBefore: Team01[];

  terminalShotNumber: number | null;
  terminalPlayerId: number | null;
  terminalShotType: string | null;
};

export type GamePointAssignmentBucket = {
  bucketKey: AssignmentBucketKey;
  playerId: TriagePlayerId;
  title: string;
  body: string;
  examples: GamePointAssignmentExample[];
};

export type GamePointAssignmentsByPlayer = Record<
  TriagePlayerId,
  {
    gamePointsWon: GamePointAssignmentBucket | null;
    gamePointsLost: GamePointAssignmentBucket | null;
    opportunitiesLost: GamePointAssignmentBucket | null;
    opponentOpportunitiesStymied: GamePointAssignmentBucket | null;
  }
>;

export type BuildGamePointAssignmentsResult = {
  byPlayerId: GamePointAssignmentsByPlayer;
  allExamples: GamePointAssignmentExample[];
};

export type BuildGamePointAssignmentsArgsignore = {
  rows: KitchenArrivalRow[];

  /**
   * Optional map of existing reason badges already computed elsewhere.
   * Keyed by rallyIndex.
   */
  badgesByRallyIndex?: Map<number, RallyBadge[]>;

  /**
   * Optional future-facing extra structures. They are accepted now so the
   * function can grow without API churn.
   */
  insightsJson?: InsightsJson | null;
  cvJson?: CvJson | null;
  pounceWindowRows?: PounceWindowRow[];
};

function otherTeam(team: Team01): Team01 {
  return team === 0 ? 1 : 0;
}

function getWinningTeam(row: KitchenArrivalRow): Team01 | null {
  return row.winnerTeam === 0 || row.winnerTeam === 1 ? row.winnerTeam : null;
}

function getLosingTeam(row: KitchenArrivalRow): Team01 | null {
  const winner = getWinningTeam(row);
  return winner == null ? null : otherTeam(winner);
}

function teamOfPlayerId(playerId: number | null | undefined): Team01 | null {
  if (playerId == null) return null;
  return playerId < 2 ? 0 : 1;
}

function isTriagePlayerId(v: number | null | undefined): v is TriagePlayerId {
  return v === 0 || v === 1 || v === 2 || v === 3;
}

function getOverlayByShotNumber(
  row: KitchenArrivalRow,
  shotNumber: number,
): ShotNumberOverlay | null {
  return (
    row.shotNumberOverlays?.find((s) => s.shotNumber === shotNumber) ?? null
  );
}

function getLatestOverlayForTeam(
  row: KitchenArrivalRow,
  team: Team01,
): ShotNumberOverlay | null {
  const arr = row.shotNumberOverlays ?? [];
  for (let i = arr.length - 1; i >= 0; i--) {
    const ov = arr[i];
    if (teamOfPlayerId(ov?.playerId) === team) return ov;
  }
  return null;
}

function getTerminalOverlay(row: KitchenArrivalRow): ShotNumberOverlay | null {
  const arr = row.shotNumberOverlays ?? [];
  return arr.length ? arr[arr.length - 1] : null;
}

function scoreTuple(v: unknown): [number, number] | null {
  if (!Array.isArray(v) || v.length < 2) return null;
  const a = v[0];
  const b = v[1];
  if (typeof a !== "number" || typeof b !== "number") return null;
  return [a, b];
}

function reasonLabelFromCode(code: AssignmentReasonCode): string {
  switch (code) {
    case "missedServe":
      return "Missed serve";
    case "missedReturn":
      return "Missed return";
    case "missed3rdDrop":
      return "Missed 3rd drop";
    case "missed3rdDrive":
      return "Missed 3rd drive";
    case "faultNet":
      return "Net / tape";
    case "faultOut":
      return "Hit out";
    case "unforcedError":
      return "Unforced error";
    case "forcedFault":
      return "Forced fault (pressure)";
    case "popupExploited":
      return "Popup exploited";
    case "terminal_winner":
      return "Finished the rally";
    case "forced_error_drawn":
      return "Forced the ending error";
    case "setup_shot":
      return "Setup shot mattered most";
    case "terminal_error":
      return "Last losing-team shot";
    default:
      return "Needs review";
  }
}

function getReasonBadgesForTeam(args: {
  rallyIndex: number;
  team: Team01;
  badgesByRallyIndex?: Map<number, RallyBadge[]>;
}): StoryFilterKey[] {
  const arr = args.badgesByRallyIndex?.get(args.rallyIndex) ?? [];
  return arr.filter((b) => b.team === args.team).map((b) => b.key);
}

function choosePrimaryLostReason(
  reasons: StoryFilterKey[],
): StoryFilterKey | null {
  const priority: StoryFilterKey[] = [
    "missedServe",
    "missedReturn",
    "missed3rdDrop",
    "missed3rdDrive",
    "popupExploited",
    "forcedFault",
    "faultNet",
    "faultOut",
    "unforcedError",
  ];

  for (const key of priority) {
    if (reasons.includes(key)) return key;
  }
  return reasons[0] ?? null;
}

function getReasonImpliedShotNumber(
  reason: StoryFilterKey | null,
): number | null {
  switch (reason) {
    case "missedServe":
      return 1;
    case "missedReturn":
      return 2;
    case "missed3rdDrop":
    case "missed3rdDrive":
      return 3;
    default:
      return null;
  }
}

function makeLostAssignment(args: {
  row: KitchenArrivalRow;
  team: Team01;
  badgesByRallyIndex?: Map<number, RallyBadge[]>;
}): GamePointAssignmentExample | null {
  const { row, team, badgesByRallyIndex } = args;
  const reasons = getReasonBadgesForTeam({
    rallyIndex: row.rallyIndex,
    team,
    badgesByRallyIndex,
  });
  const primaryReason = choosePrimaryLostReason(reasons);

  
  const rationale: string[] = [];
  const supportSignals: AssignmentSupportSignal[] = [];
  const terminalOverlay = getTerminalOverlay(row);
  const scoreBefore = scoreTuple(row.scoreBefore);
  const scoreAfter = scoreTuple(row.scoreAfter);

  if (row.scoredPoint === true) {
    rationale.push(`Team ${team} lost the rally and conceded a point.`);
  } else if (row.scoredPoint === false) {
    rationale.push(`Team ${team} lost the rally, but no point was awarded.`);
  } else {
    rationale.push(`Team ${team} lost the rally.`);
  }

  let assignedPlayerId: TriagePlayerId | null = null;
  const terminalShotNumber: number | null = terminalOverlay?.shotNumber ?? null;
  const terminalPlayerId: number | null = terminalOverlay?.playerId ?? null;
  const terminalShotType: string | null = terminalOverlay?.shotType ?? null;

  if (primaryReason) {
    rationale.push(
      `Existing losing-reason badge for Team ${team}: ${reasonLabelFromCode(primaryReason)}.`,
    );
  } else {
    rationale.push(
      "No existing losing-reason badge was found, so fallback attribution rules were used.",
    );
  }

  const impliedShotNumber = getReasonImpliedShotNumber(primaryReason);
  const impliedOverlay =
    impliedShotNumber == null
      ? null
      : getOverlayByShotNumber(row, impliedShotNumber);

  if (
    primaryReason === "missedServe" ||
    primaryReason === "missedReturn" ||
    primaryReason === "missed3rdDrop" ||
    primaryReason === "missed3rdDrive"
  ) {
    const p = impliedOverlay?.playerId ?? terminalOverlay?.playerId ?? null;
    if (isTriagePlayerId(p) && teamOfPlayerId(p) === team) {
      assignedPlayerId = p;
      rationale.push(
        `Assignment rule: ${reasonLabelFromCode(primaryReason).toLowerCase()} => blame the hitter of that shot.`,
      );
    }
  }

  if (
    assignedPlayerId == null &&
    (primaryReason === "faultNet" ||
      primaryReason === "faultOut" ||
      primaryReason === "unforcedError")
  ) {
    const p = terminalOverlay?.playerId ?? null;
    if (isTriagePlayerId(p) && teamOfPlayerId(p) === team) {
      assignedPlayerId = p;
      rationale.push(
        "Assignment rule: terminal error-type loss => blame the terminal hitter.",
      );
    }
  }


  // popup exploited block



let popupSourceShotNumber: number | null = null;

if (assignedPlayerId == null && primaryReason === "popupExploited") {
  const overlays = row.shotNumberOverlays ?? [];

  const explicitPopupSource = overlays
    .slice()
    .reverse()
    .find(
      (s) =>
        s?.popupExploited === true &&
        s.playerId != null &&
        teamOfPlayerId(s.playerId) === team,
    );

  let inferredPopupSource: ShotNumberOverlay | null = null;

  if (explicitPopupSource == null) {
    const winningTeam = getWinningTeam(row);

    let finishingAttackIndex = -1;

    if (winningTeam != null) {
      for (let i = overlays.length - 2; i >= 0; i--) {
        const ov = overlays[i];
        const next = overlays[i + 1] ?? null;

        const isWinningTeamAttack =
          teamOfPlayerId(ov?.playerId) === winningTeam;

        const directlyPrecedesLosingFault =
          next != null &&
          teamOfPlayerId(next.playerId) === team &&
          next.faultType != null;

        if (isWinningTeamAttack && directlyPrecedesLosingFault) {
          finishingAttackIndex = i;
          break;
        }
      }

      if (finishingAttackIndex < 0) {
        for (let i = overlays.length - 1; i >= 0; i--) {
          const ov = overlays[i];
          if (
            teamOfPlayerId(ov?.playerId) === winningTeam &&
            ov?.winnerType != null
          ) {
            finishingAttackIndex = i;
            break;
          }
        }
      }
    }

    if (finishingAttackIndex >= 0) {
      for (let i = finishingAttackIndex - 1; i >= 0; i--) {
        const ov = overlays[i];
        if (teamOfPlayerId(ov?.playerId) === team) {
          inferredPopupSource = ov;
          break;
        }
      }
    }
  }

  const popupSource = explicitPopupSource ?? inferredPopupSource;
  const popupPlayerId = popupSource?.playerId ?? null;
  const latestLosingOverlay = getLatestOverlayForTeam(row, team);

  popupSourceShotNumber =
    popupSource?.shotNumber ??
    latestLosingOverlay?.shotNumber ??
    null;

  if (
    isTriagePlayerId(popupPlayerId) &&
    teamOfPlayerId(popupPlayerId) === team
  ) {
    assignedPlayerId = popupPlayerId;

    rationale.push(
      explicitPopupSource != null
        ? `Assignment rule: popup exploited => blame the hitter of the popup on shot ${popupSource?.shotNumber}.`
        : popupSource?.shotNumber != null
          ? `Assignment rule: popup exploited inferred => no explicit popup marker was found, so blame the losing team's prior shot before the late finishing attack (shot ${popupSource.shotNumber}).`
          : "Assignment rule: popup exploited inferred => no explicit popup marker was found, so blame the losing team's prior shot before the late finishing attack.",
    );

    if (popupSource?.shotNumber != null) {
      supportSignals.push({
        key: "popupShotNumber",
        value: String(popupSource.shotNumber),
      });
    }
  } else {
    const fallbackPlayerId = latestLosingOverlay?.playerId ?? null;

    if (
      isTriagePlayerId(fallbackPlayerId) &&
      teamOfPlayerId(fallbackPlayerId) === team
    ) {
      assignedPlayerId = fallbackPlayerId;

      rationale.push(
        latestLosingOverlay?.shotNumber != null
          ? `Assignment rule: popup exploited fallback => could not identify popup source, so blame the latest shot by the losing team (shot ${latestLosingOverlay.shotNumber}).`
          : "Assignment rule: popup exploited fallback => could not identify popup source, so blame the latest shot by the losing team.",
      );
    }
  }
}

  // end popup exploited block




  if (assignedPlayerId == null && primaryReason === "forcedFault") {
    const p = terminalOverlay?.playerId ?? null;
    if (isTriagePlayerId(p) && teamOfPlayerId(p) === team) {
      assignedPlayerId = p;
      rationale.push(
        "Assignment rule: forced-fault loss currently defaults to the terminal hitter unless stronger evidence is added later.",
      );
    }
  }



  if (assignedPlayerId == null) {
  const latestLosingOverlay = getLatestOverlayForTeam(row, team);
  const p = latestLosingOverlay?.playerId ?? null;

  if (isTriagePlayerId(p) && teamOfPlayerId(p) === team) {
    assignedPlayerId = p;
    rationale.push(
      latestLosingOverlay?.shotNumber != null
        ? `Fallback assignment: blame the latest shot by the losing team (shot ${latestLosingOverlay.shotNumber}).`
        : "Fallback assignment: blame the latest shot by the losing team.",
    );
  }
}

if (assignedPlayerId == null) return null;


  if (terminalOverlay?.winnerType) {
    supportSignals.push({
      key: "terminalWinnerType",
      value: String(terminalOverlay.winnerType),
    });
  }
  if (terminalOverlay?.faultType) {
    supportSignals.push({
      key: "terminalFaultType",
      value: String(terminalOverlay.faultType),
    });
  }
  if (typeof row.numShots === "number") {
    supportSignals.push({ key: "numShots", value: String(row.numShots) });
  }

  return {
    rallyIndex: row.rallyIndex,
    bucketKey: "game_points_lost", // "assigned_losses",
    assignmentKind: "blame",
    team,
    assignedPlayerId,
    label: `Rally ${row.rallyIndex + 1}`,
    reasonCode: primaryReason ?? "terminal_error",

focusShotNumber:
  primaryReason === "popupExploited" ? popupSourceShotNumber : null,


reasonLabel:
  primaryReason === "popupExploited" && popupSourceShotNumber != null
    ? `Popup exploited after Shot ${popupSourceShotNumber}`
    : reasonLabelFromCode(primaryReason ?? "terminal_error"),

// reasonText:
//   primaryReason === "popupExploited"
//     ? `p${assignedPlayerId}`
//     : primaryReason != null
//       ? `p${assignedPlayerId}`
//       : terminalShotNumber != null
//         ? `Shot ${terminalShotNumber} • p${assignedPlayerId}`
//         : `p${assignedPlayerId}`,

reasonText:
  primaryReason === "popupExploited"
    ? popupSourceShotNumber != null
      ? `Shot ${popupSourceShotNumber} • p${assignedPlayerId}`
      : `p${assignedPlayerId}`
    : primaryReason != null
      ? `p${assignedPlayerId}`
      : terminalShotNumber != null
        ? `Shot ${terminalShotNumber} • p${assignedPlayerId}`
        : `p${assignedPlayerId}`,


    rationale,
    supportSignals,
    scoreBefore,
    scoreAfter,
    servingTeam: row.servingTeam,
    winnerTeam: row.winnerTeam,
    gamePointTeamsBefore: [],
    terminalShotNumber,
    terminalPlayerId,
    terminalShotType,
  };
}

function makeWonAssignment(args: {
  row: KitchenArrivalRow;
  team: Team01;
}): GamePointAssignmentExample | null {
  const { row, team } = args;
  const rationale: string[] = [];
  const supportSignals: AssignmentSupportSignal[] = [];
  const terminalOverlay = getTerminalOverlay(row);
  const scoreBefore = scoreTuple(row.scoreBefore);
  const scoreAfter = scoreTuple(row.scoreAfter);

  if (row.scoredPoint === true) {
    rationale.push(`Team ${team} won the rally and scored a point.`);
  } else if (row.scoredPoint === false) {
    rationale.push(`Team ${team} won the rally, but no point was awarded.`);
  } else {
    rationale.push(`Team ${team} won the rally.`);
  }

  let assignedPlayerId: TriagePlayerId | null = null;
  let reasonCode: AssignmentReasonCode = "terminal_winner";
  let reasonLabel = reasonLabelFromCode(reasonCode);

  const terminalPlayerId = terminalOverlay?.playerId ?? null;
  const terminalShotNumber = terminalOverlay?.shotNumber ?? null;
  const terminalShotType = terminalOverlay?.shotType ?? null;

  if (
    isTriagePlayerId(terminalPlayerId) &&
    teamOfPlayerId(terminalPlayerId) === team
  ) {
    assignedPlayerId = terminalPlayerId;
    rationale.push(
      "Assignment rule: direct closeout currently credits the terminal hitter on the winning team.",
    );
  }

  if (assignedPlayerId == null) {
    const latestOwnShot = (row.shotNumberOverlays ?? [])
      .slice()
      .reverse()
      .find((s) => s.playerId != null && teamOfPlayerId(s.playerId) === team);

    const p = latestOwnShot?.playerId ?? null;
    if (isTriagePlayerId(p)) {
      assignedPlayerId = p;
      reasonCode = "setup_shot";
      reasonLabel = reasonLabelFromCode(reasonCode);
      rationale.push(
        "Fallback assignment: credit the most recent shot by the eventual winning team.",
      );
      if (latestOwnShot) {
        supportSignals.push({
          key: "latestWinningTeamShotNumber",
          value: String(latestOwnShot.shotNumber),
        });
      }
    }
  }

  if (assignedPlayerId == null) return null;

  if (terminalOverlay?.winnerType) {
    supportSignals.push({
      key: "terminalWinnerType",
      value: String(terminalOverlay.winnerType),
    });
    if (String(terminalOverlay.winnerType).toLowerCase().includes("forced")) {
      reasonCode = "forced_error_drawn";
      reasonLabel = reasonLabelFromCode(reasonCode);
    }
  }

  if (typeof row.numShots === "number") {
    supportSignals.push({ key: "numShots", value: String(row.numShots) });
  }

  return {
    rallyIndex: row.rallyIndex,
    bucketKey: "game_points_won", //"assigned_wins",
    assignmentKind: "credit",
    team,
    assignedPlayerId,
    label: `Rally ${row.rallyIndex + 1}`,
    reasonCode,
    reasonLabel,
    // reasonText: `${reasonLabel} assigned to p${assignedPlayerId}`,
    reasonText:
  terminalShotNumber != null
    ? `Shot ${terminalShotNumber} • p${assignedPlayerId}`
    : `p${assignedPlayerId}`,
    focusShotNumber: null,
    rationale,
    supportSignals,
    scoreBefore,
    scoreAfter,
    servingTeam: row.servingTeam,
    winnerTeam: row.winnerTeam,
    gamePointTeamsBefore: [],
    terminalShotNumber,
    terminalPlayerId,
    terminalShotType,
  };
}



function didTeamLoseGamePoint(row: KitchenArrivalRow, team: Team01): boolean {
  const winnerTeam = row.winnerTeam;
  const scoredPoint = row.scoredPoint;

  return winnerTeam != null && winnerTeam !== team && scoredPoint === true;
}

function didTeamWinGamePoint(row: KitchenArrivalRow, team: Team01): boolean {
  const winnerTeam = row.winnerTeam;
  const scoredPoint = row.scoredPoint;

  return winnerTeam === team && scoredPoint === true;
}

function emptyBucketsForPlayer(playerId: TriagePlayerId) {
  return {
    gamePointsWon: {
      bucketKey: "game_points_won" as const,
      playerId,
      title: "Game Points Won",
      body: "Game points that the team converted with this player receiving the initial credit assignment.",
      examples: [],
    },
    gamePointsLost: {
      bucketKey: "game_points_lost" as const,
      playerId,
      title: "Game Points Lost",
      body: "Game points that the team lost with this player receiving the initial blame assignment.",
      examples: [],
    },
    opportunitiesLost: {
      bucketKey: "opportunities_lost" as const,
      playerId,
      title: "Opportunities Lost",
      body: "High-leverage chances where this player/team had an opening but did not convert the rally.",
      examples: [],
    },
    opponentOpportunitiesStymied: {
      bucketKey: "opponent_opportunities_stymied" as const,
      playerId,
      title: "Opponent Opportunities Stymied",
      body: "Rallies where the opponent had an opening but this player/team survived, neutralized, or later won the point.",
      examples: [],
    },
  };
}
// function emptyBucketsForPlayer(playerId: TriagePlayerId) {
//   return {
//     gamePointsWon: {
//       bucketKey: "assigned_wins" as const,
//       playerId,
//       title: "Game Points Won",
//       body: "Game points that the team converted with this player receiving the initial credit assignment.",
//       examples: [],
//     },
//     gamePointsLost: {
//       bucketKey: "assigned_losses" as const,
//       playerId,
//       title: "Game Points Lost",
//       body: "Game points that the team lost with this player receiving the initial blame assignment.",
//       examples: [],
//     },
//   };
// }

// export function buildGamePointAssignments(args: {
//   rows: KitchenArrivalRow[];
//   badgesByRallyIndex?: Map<number, RallyBadge[]>;
//   insightsJson?: InsightsJson | null;
//   cvJson?: CvJson | null;
//   pounceWindowRows?: PounceWindowRow[];
// }): BuildGamePointAssignmentsResult {
//   const { rows, badgesByRallyIndex } = args;

//   const byPlayerId: GamePointAssignmentsByPlayer = {
//     0: emptyBucketsForPlayer(0),
//     1: emptyBucketsForPlayer(1),
//     2: emptyBucketsForPlayer(2),
//     3: emptyBucketsForPlayer(3),
//   };

//   const allExamples: GamePointAssignmentExample[] = [];

//   for (const row of rows) {
//     const winningTeam = getWinningTeam(row);
//     const losingTeam = getLosingTeam(row);

//     if (winningTeam != null && didTeamWinGamePoint(row, winningTeam)) {
//       const wonExample = makeWonAssignment({
//         row,
//         team: winningTeam,
//       });

//       if (wonExample) {
//         allExamples.push(wonExample);
//         byPlayerId[wonExample.assignedPlayerId].gamePointsWon?.examples.push(
//           wonExample,
//         );
//       }
//     }

//     if (losingTeam != null && didTeamLoseGamePoint(row, losingTeam)) {
//       const lostExample = makeLostAssignment({
//         row,
//         team: losingTeam,
//         badgesByRallyIndex,
//       });

//       if (lostExample) {
//         allExamples.push(lostExample);
//         byPlayerId[lostExample.assignedPlayerId].gamePointsLost?.examples.push(
//           lostExample,
//         );
//       }
//     }
//   }

//   for (const playerId of TRIAGE_PLAYER_IDS) {
//     byPlayerId[playerId].gamePointsWon!.examples.sort(
//       (a, b) => a.rallyIndex - b.rallyIndex,
//     );
//     byPlayerId[playerId].gamePointsLost!.examples.sort(
//       (a, b) => a.rallyIndex - b.rallyIndex,
//     );

//     if (!byPlayerId[playerId].gamePointsWon!.examples.length) {
//       byPlayerId[playerId].gamePointsWon = null;
//     }
//     if (!byPlayerId[playerId].gamePointsLost!.examples.length) {
//       byPlayerId[playerId].gamePointsLost = null;
//     }
//   }

//   allExamples.sort((a, b) => a.rallyIndex - b.rallyIndex);

//   return {
//     byPlayerId,
//     allExamples,
//   };
// }

export function buildGamePointAssignments(args: {
  rows: KitchenArrivalRow[];
  badgesByRallyIndex?: Map<number, RallyBadge[]>;
  insightsJson?: InsightsJson | null;
  cvJson?: CvJson | null;
  pounceWindowRows?: PounceWindowRow[];
}): BuildGamePointAssignmentsResult {
  const { rows, badgesByRallyIndex } = args;

  const byPlayerId: GamePointAssignmentsByPlayer = {
    0: emptyBucketsForPlayer(0),
    1: emptyBucketsForPlayer(1),
    2: emptyBucketsForPlayer(2),
    3: emptyBucketsForPlayer(3),
  };

  const allExamples: GamePointAssignmentExample[] = [];

  for (const row of rows) {
    const winningTeam = getWinningTeam(row);
    const losingTeam = getLosingTeam(row);
    const servingTeam = row.servingTeam;

    // Existing: Game Points Won
    if (winningTeam != null && didTeamWinGamePoint(row, winningTeam)) {
      const wonExample = makeWonAssignment({
        row,
        team: winningTeam,
      });

      if (wonExample) {
        allExamples.push(wonExample);
        byPlayerId[wonExample.assignedPlayerId].gamePointsWon?.examples.push(
          wonExample,
        );
      }
    }

    // Existing: Game Points Lost
    if (losingTeam != null && didTeamLoseGamePoint(row, losingTeam)) {
      const lostExample = makeLostAssignment({
        row,
        team: losingTeam,
        badgesByRallyIndex,
      });

      if (lostExample) {
        allExamples.push(lostExample);
        byPlayerId[lostExample.assignedPlayerId].gamePointsLost?.examples.push(
          lostExample,
        );
      }
    }

    // New: Opportunity Lost
    // Serving team had the chance to score, but lost the rally.
    if (
      servingTeam != null &&
      losingTeam != null &&
      servingTeam === losingTeam
    ) {
      const baseLostExample = makeLostAssignment({
        row,
        team: servingTeam,
        badgesByRallyIndex,
      });

      if (baseLostExample) {
        const opportunityLostExample: GamePointAssignmentExample = {
          ...baseLostExample,
          bucketKey: "opportunities_lost",
          reasonLabel: "Opportunity lost",
          rationale: [
            `Team ${servingTeam} was serving and had the chance to score, but lost the rally.`,
            ...baseLostExample.rationale,
          ],
        };

        allExamples.push(opportunityLostExample);
        byPlayerId[
          opportunityLostExample.assignedPlayerId
        ].opportunitiesLost?.examples.push(opportunityLostExample);
      }
    }

    // New: Opponent Opportunity Stymied
    // Opponent was serving, but this team won the rally.
    if (
      servingTeam != null &&
      winningTeam != null &&
      servingTeam !== winningTeam
    ) {
      const baseWonExample = makeWonAssignment({
        row,
        team: winningTeam,
      });

      if (baseWonExample) {
        const stymiedExample: GamePointAssignmentExample = {
          ...baseWonExample,
          bucketKey: "opponent_opportunities_stymied",
          reasonLabel: "Opponent opportunity stymied",
          rationale: [
            `Opponent Team ${servingTeam} was serving and had the chance to score, but Team ${winningTeam} won the rally.`,
            ...baseWonExample.rationale,
          ],
        };

        allExamples.push(stymiedExample);
        byPlayerId[
          stymiedExample.assignedPlayerId
        ].opponentOpportunitiesStymied?.examples.push(stymiedExample);
      }
    }
  }

  for (const playerId of TRIAGE_PLAYER_IDS) {
    byPlayerId[playerId].gamePointsWon!.examples.sort(
      (a, b) => a.rallyIndex - b.rallyIndex,
    );
    byPlayerId[playerId].gamePointsLost!.examples.sort(
      (a, b) => a.rallyIndex - b.rallyIndex,
    );
    byPlayerId[playerId].opportunitiesLost!.examples.sort(
      (a, b) => a.rallyIndex - b.rallyIndex,
    );
    byPlayerId[playerId].opponentOpportunitiesStymied!.examples.sort(
      (a, b) => a.rallyIndex - b.rallyIndex,
    );

    if (!byPlayerId[playerId].gamePointsWon!.examples.length) {
      byPlayerId[playerId].gamePointsWon = null;
    }
    if (!byPlayerId[playerId].gamePointsLost!.examples.length) {
      byPlayerId[playerId].gamePointsLost = null;
    }
    if (!byPlayerId[playerId].opportunitiesLost!.examples.length) {
      byPlayerId[playerId].opportunitiesLost = null;
    }
    if (!byPlayerId[playerId].opponentOpportunitiesStymied!.examples.length) {
      byPlayerId[playerId].opponentOpportunitiesStymied = null;
    }
  }

  allExamples.sort((a, b) => a.rallyIndex - b.rallyIndex);

  return {
    byPlayerId,
    allExamples,
  };
}
